import {
  Capabilities,
  GroupPolicy,
  useSiteNameQuery,
  useGetUserGroupQuery
} from "generated/graphql";
import {
  DesktopDevicePolicyScope,
  DevicePolicyInfo,
  getCapabilityMap,
  HardwareModelProps
} from "routes/Manage/Inventory/Settings-Desktop/ProvisionedDesktopDeviceSettingsRouter";
import { useDispatch } from "react-redux";
import { desktopDeviceSettingInheritance } from "store/desktopDeviceSettingInheritance";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useEffect, useContext } from "react";

export const DesktopInheritanceLoader: React.FC<{
  device?: DevicePolicyInfo;
  settings?: Capabilities;
  sources?: GroupPolicy[];
  hardwareModel?: Partial<HardwareModelProps>;
}> = ({ device, sources, hardwareModel }) => {
  const dispatch = useDispatch();

  const sortedSources = (sources || []).sort((a, b) => {
    if (a.priority < b.priority) return 1;
    if (a.priority > b.priority) return -1;
    if (a.priority === b.priority) {
      if (a.updatedAt < b.updatedAt) return 1;
      if (a.updatedAt > b.updatedAt) return -1;
    }
    return 0;
  });

  useEffect(() => {
    dispatch(
      desktopDeviceSettingInheritance({
        key: "hardwareModel",
        scope: DesktopDevicePolicyScope.Device,
        value: hardwareModel?.name || "",
        id: hardwareModel?.id || ""
      })
    );
  }, [dispatch, hardwareModel]);

  return (
    <>
      {(sortedSources || []).map((policy: GroupPolicy) => {
        return (
          <DesktopInheritanceValues
            key={policy.id}
            policy={policy}
            deviceName={device?.name || ""}
            modelName={hardwareModel?.name || ""}
          />
        );
      })}
    </>
  );
};

const DesktopInheritanceValues: React.FC<{
  policy: GroupPolicy;
  deviceName: string;
  modelName: string;
}> = ({ policy, deviceName, modelName }) => {
  const dispatch = useDispatch();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const { data: userGroupData } = useGetUserGroupQuery({
    variables: {
      id: policy?.collectionRule?.value || "",
      tenantId: tenantId
    },
    pause:
      !policy.collectionRule?.value || !tenantId || policy.type !== "user_group"
  });
  const userGroupName = userGroupData?.getUserGroup.name ?? "";

  const { data } = useSiteNameQuery({
    variables: { id: policy?.collectionRule?.value || "" },
    pause: !policy?.collectionRule?.value || policy.type !== "site"
  });
  const siteName = data?.siteName?.name ?? "";
  let name = "Unknown Policy Name";
  if (policy.type === "device") {
    name = deviceName;
  }
  if (policy.type === "user_group") {
    name = userGroupName;
  }
  if (policy.type === "site") {
    name = siteName;
  }
  if (policy.type === "model") {
    name = modelName;
  }
  useEffect(() => {
    const capabilitiesMap = getCapabilityMap(policy.capabilities);
    if (capabilitiesMap) {
      Object.keys(capabilitiesMap).forEach(capability => {
        dispatch(
          desktopDeviceSettingInheritance({
            key: capability,
            scope: scopeToPolicyScope(policy.type),
            value: name,
            id: policy.collectionRule?.value || ""
          })
        );
      });
    }
  }, [dispatch, policy, name]);

  return <></>;
};

const scopeToPolicyScope = (scope: string) => {
  switch (scope) {
    case "site":
      return DesktopDevicePolicyScope.Site;
    case "group":
      return DesktopDevicePolicyScope.Group;
    case "model":
      return DesktopDevicePolicyScope.Model;
    case "user_group":
      return DesktopDevicePolicyScope.User_Group;
    default:
      return DesktopDevicePolicyScope.Device;
  }
};
